import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import ConfirmationDialog from 'routes/Pages/components/ConfirmDialogs/ConfirmationDialog';
import { detailedM3Options } from '../Data/HFDropDowdData';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const autoCompleteSlotProps = {
  paper: {
    sx: {
      '& .MuiAutocomplete-option': {
        fontSize: '0.82rem',
        fontWeight: 500,
      },
    },
  },
};

const DEFAULT_NEW_ROWDATA = {
  productNo: '',
  productName: null,
  m3: '',
  notes: '',
};

// Ops Calculation Helpers
export const M3_PER_TONNE = 1.15;
export const MAX_REASONABLE_VALUE = 100000;

export const normalizeQty = raw => {
  const n = parseFloat(raw);
  if (isNaN(n)) return 0;
  return n >= MAX_REASONABLE_VALUE ? n / 1000 : n;
};

export const isTonnesUom = (uom) =>
  String(uom ?? '').trim().toLowerCase() === 'tonnes';

export const calcTotals = (rows = [], uom = 'M3') => {
  const sum = (rows ?? []).reduce((acc, r) => acc + normalizeQty(r?.m3), 0);

  if (isTonnesUom(uom)) {
    const totalValue = sum;                   // tonnes
    const totalM3 = totalValue * M3_PER_TONNE; // 1 tonne = 1.15 m3
    return { totalValue, totalM3 };
  }

  return { totalValue: 0, totalM3: sum };
};

const DetailedM3Table = ({
  productionData,
  setProductionData,
  currDailyHarvestingData,
  handleChangeDailyHarvestingData,
}) => {
  const [openAddNewRow, setOpenAddNewRow] = useState(false);
  const [newRowData, setNewRowData] = useState(DEFAULT_NEW_ROWDATA);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const uom = currDailyHarvestingData['unitOfMeasure'] || 'M3';
  const isTonnes = uom === 'Tonnes';

  const handleM3RowDataChange = (index, key, value) => {
    const updatedData = [...productionData];
    updatedData[index][key] = value;
    setProductionData(updatedData);
  };

  const handleAddNewRow = () => {
    const newRow = {
      ...DEFAULT_NEW_ROWDATA,
      productNo: `${productionData.length + 1}`,
    };
    setProductionData(prev => [...prev, newRow]);
  };

  const handleNewRowDataChange = (key, value) => {
    setNewRowData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDeleteRow = index => {
    setOpenDialog(true);
    setDeleteIndex(index);
  };

  function confirmationFunction(index) {
    if (index === null || index === undefined) return;

    const rowToDelete = productionData[index];
    const updatedData = productionData.filter((_, i) => i !== index);
    setProductionData(updatedData);

    if (rowToDelete?.id) {
      customAxios.post(
        `/Ops/DeleteTasksProductionRecord`,
        { id: rowToDelete.id },
        { headers: authHeader() }
      );
    }

    setOpenDialog(false);
    setDeleteIndex(null);
  }

  return (
    <Box sx={{ paddingBlock: '1rem' }}>
      <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: '10px', paddingBlock: '0.5rem' }}>
        <Typography variant="subtitle1">Detailed Production Table ({uom})</Typography>
        <Button size="small" onClick={handleAddNewRow} variant="contained">
          Add New Row
        </Button>
      </Box>

      {/* Warning Unit Toggle Banner */}
      <Box sx={styles.warnBanner}>
        <Box sx={styles.warnIconWrap}>
          <WarningAmberRoundedIcon sx={styles.warnIcon} />
        </Box>

        <Box sx={styles.warnContent}>
          <Typography sx={styles.warnTitle}>
            Check your machine&rsquo;s output unit
          </Typography>
          <Typography sx={styles.warnText}>
            Does this machine report its output in M3 or Tonnes? Select the correct
            unit before entering values below.
          </Typography>

          <Box sx={styles.switchRow}>
            <Typography sx={[styles.uomLabel, !isTonnes && styles.uomActive]}>
              M3
            </Typography>

            <Switch
              size="small"
              checked={isTonnes}
              onChange={event =>
                handleChangeDailyHarvestingData(
                  'unitOfMeasure',
                  event.target.checked ? 'Tonnes' : 'M3'
                )
              }
              sx={styles.uomSwitch}
            />

            <Typography sx={[styles.uomLabel, isTonnes && styles.uomActive]}>
              Tonnes
            </Typography>
          </Box>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: 1,
        }}>
        {productionData.length === 0 && !openAddNewRow ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
              gap: '4px',
            }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748B' }}>
              No products added yet
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8' }}>
              Select a Forest or SP Number to auto-load products, or click "Add New Row" to add one manually.
            </Typography>
          </Box>
        ) : (
          <Table border={1} size="small" sx={{ border: '1px solid #ccc', fontSize: '0.825rem' }}>
            <TableHead>
              <TableRow>
                <TableCell>Sr.No</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>{uom}</TableCell>
                <TableCell width="35%">Notes</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productionData.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.productNo || index + 1}</TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      disablePortal
                      name={'productName'}
                      value={item.productName || null}
                      options={detailedM3Options}
                      onChange={(event, newValue) => handleM3RowDataChange(index, 'productName', newValue)}
                      sx={{ fontSize: '0.8rem' }}
                      renderInput={params => <TextField {...params} size="small" fullWidth />}
                      fullWidth
                      slotProps={{ ...autoCompleteSlotProps }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.m3}
                      onChange={e => handleM3RowDataChange(index, 'm3', e.target.value)}
                      fullWidth
                      type="number"
                      placeholder={`Enter ${uom}`}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.notes}
                      onChange={e => handleM3RowDataChange(index, 'notes', e.target.value)}
                      fullWidth
                      type="text"
                      multiline
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleDeleteRow(index)} color="error">
                      <DeleteRoundedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {openAddNewRow && (
                <TableRow>
                  <TableCell>{newRowData.productNo}</TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      disablePortal
                      name={'productName'}
                      value={newRowData.productName || null}
                      options={detailedM3Options}
                      onChange={(event, newValue) => handleNewRowDataChange('productName', newValue)}
                      sx={{ fontSize: '0.8rem' }}
                      renderInput={params => <TextField {...params} size="small" fullWidth />}
                      fullWidth
                      slotProps={{ ...autoCompleteSlotProps }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={newRowData.m3}
                      onChange={e => handleNewRowDataChange('m3', e.target.value)}
                      fullWidth
                      type="number"
                      placeholder={`Enter ${uom}`}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={newRowData.notes}
                      onChange={e => handleNewRowDataChange('notes', e.target.value)}
                      fullWidth
                      type="text"
                      multiline
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => setOpenAddNewRow(false)} color="error">
                      <DeleteRoundedIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <ConfirmationDialog
        open={openDialog}
        confirmationText="Do you want to delete this item?"
        declineFunction={() => setOpenDialog(false)}
        setOpen={setOpenDialog}
        confirmationFun={() => confirmationFunction(deleteIndex)}
      />
    </Box>
  );
};

export default DetailedM3Table;

const styles = {
  warnBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    backgroundColor: '#FFFBEB',
    border: '1px solid #FCD34D',
    borderLeft: '4px solid #F59E0B',
    borderRadius: '0.75rem',
    padding: '0.875rem 1rem',
    marginBottom: '1rem',
  },
  warnIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#FEF3C7',
    marginTop: '2px',
  },
  warnIcon: {
    fontSize: 20,
    color: '#D97706',
  },
  warnContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  warnTitle: {
    fontWeight: 700,
    fontSize: '0.9rem',
    color: '#92400E',
    lineHeight: 1.3,
    marginBottom: '0.2rem',
  },
  warnText: {
    color: '#A16207',
    fontSize: '0.78rem',
    lineHeight: 1.4,
    marginBottom: '0.75rem',
  },
  switchRow: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: '0.25rem',
    backgroundColor: '#FFFFFF',
    border: '1px solid #FCD34D',
    borderRadius: '999px',
    padding: '2px 10px 2px 14px',
  },
  uomSwitch: {
    '& .MuiSwitch-switchBase.Mui-checked': { color: '#B45309' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#F59E0B',
      opacity: 1,
    },
    '& .MuiSwitch-track': { backgroundColor: '#CBD5E1', opacity: 1 },
  },
  uomLabel: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#A16207',
    userSelect: 'none',
    transition: 'color 120ms ease, font-weight 120ms ease',
  },
  uomActive: {
    fontWeight: 800,
    color: '#92400E',
  },
};